/**
 * VelocityCard — reusable 8-week sparkline.
 *
 * One inline-SVG component used across the app to surface "is this
 * organisation's pulse trending up or down?" for any time-bucketed metric.
 *
 * Used today on:
 *   - /goals?scope=org   — approval velocity (objectives approved per week)
 *   - /reviews           — review-completion velocity (reviews submitted per week)
 *   - /calibration       — post-meeting rating-shift volume (per week)
 *
 * The shape of `velocity` matches the backend `_event_velocity` helper:
 *   {
 *     weeks: [{week_start, week_end, count, drift?}],
 *     total: number,
 *     total_drift: number,
 *     delta_wow: number,
 *   }
 *
 * `interpret(velocity)` returns the short HR-leadership sentence shown on
 * the right-hand side. Every consumer can tailor language to its domain.
 */
import React from "react";
import { cn } from "@/lib/utils";

export default function VelocityCard({
  velocity,
  overline = "Velocity · last 8 weeks",
  unit = "event",
  unitPlural = "events",
  primaryColor = "#10b981",
  driftLabel = "Drift signal",
  driftColor = "#f43f5e",
  totalsLabel = ({ total, drift }) =>
    `8wk total: ${total} ${unitPlural} · ${drift} drift${drift === 1 ? "" : "s"}`,
  interpret,
  testId = "velocity-card",
}) {
  const weeks = velocity?.weeks || [];
  if (weeks.length === 0) return null;
  const thisWeek = weeks[weeks.length - 1];
  const lastWeek = weeks.length >= 2 ? weeks[weeks.length - 2] : null;
  const delta = velocity.delta_wow || 0;
  const deltaTone =
    delta > 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
    delta < 0 ? "text-rose-700 bg-rose-50 border-rose-200" :
    "text-slate-600 bg-slate-100 border-slate-200";
  const deltaSign = delta > 0 ? "+" : "";

  const maxCount = Math.max(1, ...weeks.map((w) => w.count));

  // SVG geometry
  const W = 560, H = 80, PAD_X = 8, PAD_Y = 14;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const barW = innerW / weeks.length - 8;
  const xFor = (i) => PAD_X + (innerW / weeks.length) * i + 4;
  const yFor = (val) => PAD_Y + innerH - (val / maxCount) * innerH;

  const interpretation = interpret
    ? interpret(velocity)
    : (velocity.total === 0
      ? "No activity yet this period."
      : delta > 0 ? "Trending up week-over-week." :
        delta < 0 ? "Slower than last week — worth a look." :
        "Steady cadence.");

  return (
    <div className="axia-card p-5 space-y-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="axia-overline">{overline}</div>
          <div className="flex items-baseline gap-3 mt-1.5">
            <div className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid={`${testId}-this-week`}>
              {thisWeek.count}
            </div>
            <div className="text-sm text-slate-500">
              {thisWeek.count === 1 ? unit : unitPlural} this week
            </div>
            {lastWeek && (
              <span
                className={cn(
                  "inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border font-mono font-medium",
                  deltaTone,
                )}
                data-testid={`${testId}-delta`}
                title={`vs ${lastWeek.count} last week`}
              >
                {deltaSign}{delta} WoW
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 leading-snug max-w-[280px]" data-testid={`${testId}-interpretation`}>
          {interpretation}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-20"
        role="img"
        aria-label={overline}
        data-testid={`${testId}-sparkline`}
      >
        <line x1={PAD_X} y1={H - PAD_Y} x2={W - PAD_X} y2={H - PAD_Y} stroke="#e2e8f0" strokeWidth={1} />
        {weeks.map((w, i) => {
          const y = yFor(w.count);
          const h = H - PAD_Y - y;
          const isCurrent = i === weeks.length - 1;
          return (
            <g key={w.week_start}>
              <rect
                x={xFor(i)}
                y={y}
                width={Math.max(barW, 8)}
                height={Math.max(h, w.count > 0 ? 2 : 0)}
                rx={2}
                fill={isCurrent ? "#0F172A" : primaryColor}
                opacity={isCurrent ? 1 : 0.85}
              >
                <title>
                  {`${w.week_start} → ${w.week_end}\n${w.count} ${unitPlural}` +
                    (w.drift ? ` · ${w.drift} drift` : "")}
                </title>
              </rect>
              {(w.drift || 0) > 0 && (
                <circle
                  cx={xFor(i) + Math.max(barW, 8) / 2}
                  cy={PAD_Y - 4}
                  r={3}
                  fill={driftColor}
                >
                  <title>{`${w.drift} ${driftLabel.toLowerCase()} in ${w.week_start} → ${w.week_end}`}</title>
                </circle>
              )}
            </g>
          );
        })}
        {weeks.map((w, i) => (
          i % 2 === weeks.length % 2 ? (
            <text
              key={`lbl-${w.week_start}`}
              x={xFor(i) + Math.max(barW, 8) / 2}
              y={H - 2}
              textAnchor="middle"
              fontSize={9}
              fill="#94a3b8"
            >
              {w.week_start.slice(5)}
            </text>
          ) : null
        ))}
      </svg>

      <div className="flex items-center gap-4 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: primaryColor, opacity: 0.85 }} />
          {unitPlural[0].toUpperCase() + unitPlural.slice(1)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-900" />
          This week
        </span>
        {(velocity.total_drift || 0) > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: driftColor }} />
            {driftLabel}
          </span>
        )}
        <span className="ml-auto font-mono">
          {totalsLabel({ total: velocity.total || 0, drift: velocity.total_drift || 0 })}
        </span>
      </div>
    </div>
  );
}
